import { createStore } from "vuex";
import auth from "@/store/modules/auth";
import axios from "@/axios.js";

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

/** Matches loginUser / logout headers; falls back when localStorage hasn't been read yet */
function axiosAuthHeader() {
  return axios.defaults.headers.common.Authorization || localStorage.getItem('auth_token') || '';
}

export default createStore({
  state: {
    products: [],
    reviewEntities: [],
  },
  getters: {
    getUser(state) {
      return state.user;
    },
    getProducts(state) {
      return state.products;
    },
    hasReviewEntities(state) {
      return state.reviewEntities && state.reviewEntities.length > 0;
    },
    getReviewEntities(state) {
      return state.reviewEntities;
    },
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setProducts(state, products) {
      state.products = products;
    },
    setReviewEntities(state, reviewEntities) {
      state.reviewEntities = reviewEntities;
    },
  },
  actions: {
    async updateTeamName(_, payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .patch(`${BASE_URL}/profile/team`, { team: { name: payload.name } }, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    /** Paid plan changes without new checkout: Visibility → Intelligence (interim); use Stripe for Free → paid. */
    async updateTeamPlan(_, payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .patch(
            `${BASE_URL}/profile/team/plan`,
            { team: { plan_tier: payload.plan_tier } },
            config
          )
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    async createStripeCheckoutSession(_, payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${BASE_URL}/stripe/checkout_sessions`,
            { checkout_session: { plan_tier: payload.plan_tier } },
            config
          )
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    async completeStripeCheckout(_, payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${BASE_URL}/stripe/checkout_sessions/complete`,
            { session_id: payload.session_id },
            config
          )
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    async cancelStripeSubscription(_, _payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/stripe/subscription/cancel`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    async upgradeStripeSubscriptionToIntelligence(_, _payload) {
      const config = {
        headers: {
          Authorization: axiosAuthHeader(),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/stripe/subscription/upgrade_to_intelligence`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error));
      });
    },
    async suggestProducts({ commit }, brandInfo) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/aeo/suggest_products`, { brand_info: brandInfo }, config)
          .then((response) => {
            if (response.data.success) {
              // Handle different response structures
              const products = response.data.products?.products || 
                              response.data.products || 
                              [];
              commit("setProducts", Array.isArray(products) ? products : []);
            }
            resolve(response.data);
          })
          .catch((error) => {
            // Handle authentication errors (401) or redirects (302)
            if (error.response) {
              // If we get a 401 or 302, the axios interceptor should handle it
              // But we still need to reject with a proper error structure
              const errorData = error.response.data || { 
                success: false, 
                error: error.response.status === 401 
                  ? 'Unauthorized. Please log in again.' 
                  : 'An error occurred while fetching product suggestions'
              };
              reject(error);
            } else {
              // Network error or other non-HTTP error
              reject({
                response: {
                  data: {
                    success: false,
                    error: 'Network error. Please check your connection and try again.'
                  }
                }
              });
            }
          });
      });
    },
    async fetchScanEnrichment(_, payload) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/aeo/scan_enrichment`, payload, config)
          .then((response) => resolve(response.data))
          .catch((error) => {
            reject(error?.response?.data || { success: false, error: 'Failed to enrich scan animation' });
          });
      });
    },
    async createReviewEntity({ commit, dispatch }, payload) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities`, { review_entity: payload }, config)
          .then(async (response) => {
            // Refresh review entities list to update navbar
            try {
              await dispatch('fetchReviewEntities');
            } catch (error) {
              console.error('Failed to refresh review entities after creation:', error);
            }
            resolve(response.data);
          })
          .catch((error) => {
            reject(error?.response?.data || { error: 'Failed to create review entity' });
          });
      });
    },
    async deleteAllReviewEntities({ commit }) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .delete(`${BASE_URL}/review_entities/destroy_all`, config)
          .then((response) => {
            // Clear review entities from state
            commit('setReviewEntities', []);
            resolve(response.data);
          })
          .catch((error) => reject(error?.response?.data || { error: 'Failed to delete review entities' }));
      });
    },
    async fetchReviewEntities({ commit }, params = {}) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      
      // Build URL with optional query parameters
      const query = new URLSearchParams();
      if (params.limit) query.set('limit', params.limit);
      if (params.include_archived) query.set('include_archived', '1');
      const qs = query.toString();
      const url = `${BASE_URL}/review_entities${qs ? `?${qs}` : ''}`;
      
      return new Promise((resolve, reject) => {
        axios
          .get(url, config)
          .then((response) => {
            // Only update global picker state when fetching the default (non-limited)
            // list. Always strip archived so navbar/hasReviewEntities stay correct
            // even if a caller passed include_archived.
            if (!params.limit && response.data.success && response.data.review_entities) {
              const forPicker = response.data.review_entities.filter(
                (e) => !e.archived_at && e.status !== 'archived'
              );
              commit('setReviewEntities', forPicker);
            }
            resolve(response.data);
          })
          .catch((error) => reject(error?.response?.data || { error: 'Failed to fetch review entities' }));
      });
    },
    async fetchReviewEntity({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASE_URL}/review_entities/${reviewEntityId}`, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to fetch review entity' }));
      });
    },
    async recordEntityVisit({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities/${reviewEntityId}/visit`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to record entity visit' }));
      });
    },
    async pauseReviewEntity({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities/${reviewEntityId}/pause`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to pause review entity' }));
      });
    },
    async unpauseReviewEntity({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities/${reviewEntityId}/unpause`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to unpause review entity' }));
      });
    },
    async archiveReviewEntity({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities/${reviewEntityId}/archive`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to archive review entity' }));
      });
    },
    async unarchiveReviewEntity({ commit }, reviewEntityId) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/review_entities/${reviewEntityId}/unarchive`, {}, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to unarchive review entity' }));
      });
    },
    async updateReviewEntityFolder({ commit }, { reviewEntityId, folder }) {
      const config = {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      };
      return new Promise((resolve, reject) => {
        axios
          .patch(`${BASE_URL}/review_entities/${reviewEntityId}/folder`, { folder }, config)
          .then((response) => resolve(response.data))
          .catch((error) => reject(error?.response?.data || { error: 'Failed to update folder' }));
      });
    },
  },
  modules: {
    auth,
  },
});
